import React, { useState, useEffect, useRef } from 'react';
import { getTheme } from '../theme';
import { API, authHeaders } from '../utils/helpers';
import SmsConversation from './SmsConversation';
import TextingOff from './TextingOff';
import { useTextingReady } from '../utils/texting';

export default function Messages({ theme = 'dark', currentUser = null, polledConversations = null }) {
  const isDark = theme === 'dark';
  const T = getTheme(theme);
  const textingReady = useTextingReady();

  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'unread' | 'flagged'
  const [search, setSearch] = useState('');
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [hasOpenedSelected, setHasOpenedSelected] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [candidateTickets, setCandidateTickets] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeQuery, setComposeQuery] = useState('');
  const [composeResults, setComposeResults] = useState([]);
  const [composeSearching, setComposeSearching] = useState(false);
  const [newTarget, setNewTarget] = useState(null); // { phone, name, customerId } for a conversation with no messages yet
  const composeSearchTimer = useRef(null);
  // Guards the open-the-newest-conversation-on-entry behaviour below so it
  // happens exactly once per visit to this screen. Without it the 8-second
  // background poll would re-assert the newest thread every cycle and yank
  // Manny out of whatever conversation he had clicked into.
  const autoSelectedRef = useRef(false);

  const loadConversations = (silent = false) => {
    if (!silent) setLoading(true);
    const token = localStorage.getItem('sb_token');
    fetch(`${API}/api/sms/conversations`, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(list => setConversations(Array.isArray(list) ? list : []))
      .catch(err => console.error('Failed to load conversations:', err))
      .finally(() => { if (!silent) setLoading(false); });
  };

  // Load once on entry, so the screen is current the moment it opens.
  useEffect(() => {
    loadConversations();
  }, []);

  // No timer of its own any more (26 Sep). This screen used to poll the same
  // route every 8 s while App.js polled it every 15 s for the badge and chime --
  // two timers, about 11 requests a minute, for one list. App.js is now the only
  // poller and hands each fresh list down here. The OPEN thread still refreshes
  // itself every 8 s in SmsConversation, so a conversation being read is as live
  // as before; only the list behind it moves to the shared cycle.
  //
  // Refreshes this screen triggers itself -- after marking read, sending, or
  // toggling a row -- still fetch at once through loadConversations.
  useEffect(() => {
    if (Array.isArray(polledConversations)) setConversations(polledConversations);
  }, [polledConversations]);

  const refreshAfter = (fn) => {
    fn();
    setTimeout(loadConversations, 400);
  };

  const filtered = conversations.filter(c => {
    if (filter === 'unread' && Number(c.unread_count) === 0) return false;
    if (filter === 'flagged' && !c.flagged) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const qDigits = q.replace(/\D/g, '');
      const phone = (c.customer_phone || '').toLowerCase();
      const name = (c.customer_name || '').toLowerCase();
      const phoneMatch = qDigits.length > 0 && phone.includes(qDigits);
      const nameMatch = name.length > 0 && name.includes(q);
      if (!phoneMatch && !nameMatch) return false;
    }
    return true;
  });

  // Counts conversations with at least one unread message, not the raw sum
  // of unread messages — otherwise this number can exceed the visible
  // conversation count and reads as confusing/wrong.
  const totalUnread = conversations.filter(c => Number(c.unread_count || 0) > 0).length;
  const existingSelected = conversations.find(c => c.customer_phone === selectedPhone) || null;
  // If compose freshly resolved a customerId for the currently-selected phone,
  // merge it in even when a conversation already exists — otherwise a customer
  // with prior message history (found again via compose) silently loses the
  // fresh customerId to the existing record's stale (possibly null) value.
  const selected = existingSelected
    ? (newTarget && newTarget.phone === selectedPhone && newTarget.customerId
        ? { ...existingSelected, last_customer_id: newTarget.customerId, customer_name: existingSelected.customer_name || newTarget.name }
        : existingSelected)
    : (newTarget && newTarget.phone === selectedPhone
        ? { customer_phone: newTarget.phone, customer_name: newTarget.name || '', last_customer_id: newTarget.customerId || null, unread_count: 0, flagged: false }
        : null);

  const selectConversation = (phone, name = null, customerId = null) => {
    setSelectedPhone(phone);
    setHasOpenedSelected(false); // first click = view only, no read state change
    setCandidateTickets([]);
    setSelectedTicketId(null);
    setTicketDetail(null);
    setNewTarget(name !== null ? { phone, name, customerId } : null);
    setComposeOpen(false);
    setComposeQuery('');
    setComposeResults([]);
  };

  // Open the newest conversation as soon as the list arrives, rather than
  // landing on an empty "Select a conversation to view" pane. The list comes
  // back newest-first, but sort defensively on last_created_at rather than
  // trusting order — if the server's ordering ever changes, the wrong thread
  // opening on entry is a confusing bug to chase.
  //
  // This deliberately does NOT mark anything read: hasOpenedSelected stays
  // false, so an unread thread that opens itself stays unread until Manny
  // actually clicks into it. Auto-clearing the unread badge for a message
  // nobody has read yet would be worse than the blank pane this replaces.
  useEffect(() => {
    if (autoSelectedRef.current) return;
    if (loading || selectedPhone || conversations.length === 0) return;
    const newest = [...conversations].sort(
      (a, b) => new Date(b.last_created_at) - new Date(a.last_created_at)
    )[0];
    if (!newest) return;
    autoSelectedRef.current = true;
    selectConversation(newest.customer_phone);
  }, [conversations, loading, selectedPhone]);

  useEffect(() => {
    if (!selectedPhone) return;
    // Set the fallback immediately from the conversation's already-known
    // last ticket, rather than only inside the candidate-tickets fetch's
    // .then(). Previously, if that fetch failed outright (network error,
    // non-JSON response, auth timing), the whole .then() chain never ran and
    // selectedTicketId silently stayed null — disabling Invoice attach even
    // when the conversation had a perfectly valid linked ticket.
    setSelectedTicketId(selected?.last_ticket_id || null);
    const token = localStorage.getItem('sb_token');
    fetch(`${API}/api/sms/candidate-tickets?phone=${encodeURIComponent(selectedPhone)}`, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(list => {
        const candidates = Array.isArray(list) ? list : [];
        setCandidateTickets(candidates);
        const fallback = selected?.last_ticket_id || (candidates[0] && candidates[0].id) || null;
        if (fallback) setSelectedTicketId(fallback);
      })
      .catch(() => setCandidateTickets([]));
  }, [selectedPhone]);

  useEffect(() => {
    setTicketDetail(null);
    if (!selectedTicketId) return;
    fetch(`${API}/api/tickets/${selectedTicketId}`, { credentials: 'include', headers: authHeaders() })
      .then(r => r.json())
      .then(setTicketDetail)
      .catch(() => setTicketDetail(null));
  }, [selectedTicketId]);

  const markSelectedRead = () => {
    if (!selectedPhone) return;
    const token = localStorage.getItem('sb_token');
    refreshAfter(() => {
      fetch(`${API}/api/sms/read-status`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ phone: selectedPhone, read: true }),
      }).catch(() => {});
    });
  };

  const markSelectedUnread = () => {
    if (!selectedPhone) return;
    const token = localStorage.getItem('sb_token');
    refreshAfter(() => {
      fetch(`${API}/api/sms/read-status`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ phone: selectedPhone, read: false }),
      }).catch(() => {});
    });
  };

  // Toggle read/unread directly from a list row, without opening the thread.
  // Optimistically updates the row's unread count so the click feels instant,
  // then reconciles against the server response.
  const toggleRowReadStatus = (e, phone, currentlyUnread) => {
    e.stopPropagation();
    const token = localStorage.getItem('sb_token');
    const newRead = currentlyUnread; // currently unread -> mark read; currently read -> mark unread
    setConversations(prev => prev.map(c => c.customer_phone === phone
      ? { ...c, unread_count: newRead ? 0 : Math.max(1, Number(c.unread_count || 0) || 1) }
      : c));
    fetch(`${API}/api/sms/read-status`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ phone, read: newRead }),
    })
      .then(loadConversations)
      .catch(() => loadConversations());
  };

  const markAllRead = async () => {
    setMarkingAll(true);
    try {
      const token = localStorage.getItem('sb_token');
      await fetch(`${API}/api/sms/mark-all-read`, {
        method: 'PUT', credentials: 'include',
        headers: { Authorization: `Bearer ${token}` },
      });
      loadConversations();
    } catch (_) {}
    setMarkingAll(false);
  };

  const acknowledgeFailures = () => {
    if (!selectedPhone) return;
    const token = localStorage.getItem('sb_token');
    fetch(`${API}/api/sms/acknowledge-failures`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ phone: selectedPhone }),
    }).catch(() => {});
  };

  const handleThreadInteraction = () => {
    if (hasOpenedSelected) return;
    setHasOpenedSelected(true);
    markSelectedRead(); // already reloads conversations shortly after — covers the failure-icon clear too
    if (selected?.has_failed) acknowledgeFailures();
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') return;
      if (!['ArrowUp', 'ArrowDown'].includes(e.key)) return;
      if (filtered.length === 0) return;
      const idx = filtered.findIndex(c => c.customer_phone === selectedPhone);
      let nextIdx;
      if (idx === -1) nextIdx = 0;
      else if (e.key === 'ArrowDown') nextIdx = Math.min(idx + 1, filtered.length - 1);
      else nextIdx = Math.max(idx - 1, 0);
      e.preventDefault();
      selectConversation(filtered[nextIdx].customer_phone);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [filtered, selectedPhone]);

  // ── Compose new message ────────────────────────────────────────────────────
  const runComposeSearch = (q) => {
    setComposeQuery(q);
    if (composeSearchTimer.current) clearTimeout(composeSearchTimer.current);
    if (q.trim().length < 2) { setComposeResults([]); return; }
    composeSearchTimer.current = setTimeout(() => {
      setComposeSearching(true);
      const token = localStorage.getItem('sb_token');
      fetch(`${API}/api/customers?q=${encodeURIComponent(q.trim())}`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.json())
        .then(list => setComposeResults(Array.isArray(list) ? list : []))
        .catch(() => setComposeResults([]))
        .finally(() => setComposeSearching(false));
    }, 300);
  };

  const openCompose = () => {
    setComposeOpen(true);
    setComposeQuery('');
    setComposeResults([]);
  };

  const isLikelyPhone = (str) => str.replace(/\D/g, '').length >= 7;
  const cleanPhone = (str) => str.replace(/\D/g, '');
  const formatPhoneDisplay = (digits) => {
    const d = (digits || '').replace(/^1/, '').slice(-10);
    if (d.length !== 10) return digits || '';
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  };

  const bg      = isDark ? '#0f1724' : '#f4f4ef';
  const panelBg = isDark ? '#1a2336' : '#ffffff';
  const border  = isDark ? '#2e3d58' : '#e0e0d8';
  const itemBg  = isDark ? '#243050' : '#f0f0e8';
  const textPri = T.text;
  const textSub = T.textSub;

  const fmtListTime = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    const daysAgo = Math.floor((now - d) / 86400000);
    if (daysAgo < 7) return d.toLocaleDateString('en-US', { weekday: 'short' });
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const initials = (name) => {
    if (!name) return '#';
    const parts = name.trim().split(/\s+/);
    return parts.length > 1
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : parts[0].slice(0, 2).toUpperCase();
  };

  return (
    <div style={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: textPri }}>Messages</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 14, color: textSub }}>
            {conversations.length} conversation{conversations.length !== 1 ? 's' : ''}
            {totalUnread > 0 && ` · ${totalUnread} unread`}
          </span>
          <button onClick={markAllRead} disabled={markingAll || totalUnread === 0}
            style={{ fontSize: 13, padding: '6px 12px', borderRadius: 6, border: `1px solid ${border}`,
              background: 'transparent', color: totalUnread === 0 ? textSub : '#3498db',
              cursor: totalUnread === 0 ? 'default' : 'pointer', opacity: totalUnread === 0 ? 0.5 : 1 }}>
            {markingAll ? 'Marking…' : 'Mark all read'}
          </button>
        </div>
      </div>

      {!textingReady && <div style={{ marginBottom: 16 }}><TextingOff theme={theme} /></div>}

      <div style={{ flex: 1, minHeight: 0, display: 'flex', border: `1px solid ${border}`, borderRadius: 10, overflow: 'hidden', background: panelBg }}>

        {/* Conversation list */}
        <div style={{ width: 300, flexShrink: 0, borderRight: `1px solid ${border}`, display: 'flex', flexDirection: 'column' }}>

          {composeOpen ? (
            <>
              <div style={{ padding: '10px 12px', borderBottom: `1px solid ${border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: textPri }}>New message</span>
                  <button onClick={() => { setComposeOpen(false); setComposeQuery(''); setComposeResults([]); }}
                    style={{ fontSize: 13, color: textSub, background: 'transparent', border: 'none', cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
                <input
                  autoFocus
                  value={composeQuery}
                  onChange={e => runComposeSearch(e.target.value)}
                  placeholder="Search customer name or enter phone number…"
                  style={{ width: '100%', background: isDark ? '#243050' : '#f4f4ef', border: `1px solid ${border}`,
                    borderRadius: 6, padding: '7px 10px', color: textPri, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {composeSearching ? (
                  <div style={{ padding: 20, textAlign: 'center', fontSize: 13, color: textSub, fontStyle: 'italic' }}>Searching…</div>
                ) : composeResults.length > 0 ? (
                  composeResults.map(c => (
                    <div key={c.id} onClick={() => c.phone && selectConversation(cleanPhone(c.phone), c.name, c.id)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                        borderBottom: `1px solid ${border}`, cursor: c.phone ? 'pointer' : 'default',
                        opacity: c.phone ? 1 : 0.5 }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                        background: itemBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 13, color: textPri, fontWeight: 600 }}>
                        {initials(c.name)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, color: textPri, fontWeight: 500 }}>{c.name}</div>
                        <div style={{ fontSize: 13, color: textSub }}>{c.phone ? formatPhoneDisplay(cleanPhone(c.phone)) : 'No mobile number on file'}</div>
                      </div>
                    </div>
                  ))
                ) : composeQuery.trim().length >= 2 ? (
                  <div style={{ padding: '10px 12px' }}>
                    <div style={{ fontSize: 13, color: textSub, fontStyle: 'italic', marginBottom: 8 }}>No customers matched.</div>
                    {isLikelyPhone(composeQuery) && (
                      <div onClick={() => selectConversation(cleanPhone(composeQuery), '')}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                          border: `1px solid ${border}`, borderRadius: 6, cursor: 'pointer' }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                          background: itemBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 15, color: textPri }}>#</div>
                        <div style={{ fontSize: 14, color: textPri }}>Message {formatPhoneDisplay(cleanPhone(composeQuery))}</div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ padding: 20, textAlign: 'center', fontSize: 13, color: textSub, fontStyle: 'italic' }}>
                    Type a name to search customers, or enter a phone number directly.
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div style={{ padding: '10px 12px', borderBottom: `1px solid ${border}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[{ k: 'all', l: 'All' }, { k: 'unread', l: 'Unread' }, { k: 'flagged', l: 'Flagged' }].map(f => (
                    <button key={f.k} onClick={() => setFilter(f.k)}
                      style={{ fontSize: 13, padding: '4px 10px', borderRadius: 14,
                        border: `1px solid ${filter === f.k ? '#3498db' : border}`,
                        background: filter === f.k ? (isDark ? '#0d1e2e' : '#eff6ff') : 'transparent',
                        color: filter === f.k ? '#3498db' : textSub, cursor: 'pointer' }}>
                      {f.l}
                    </button>
                  ))}
                </div>
                {/* Search + New message live side by side so starting a new
                    conversation doesn't require a long mouse trip from the
                    top-right corner of the screen. */}
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search name or phone…"
                    style={{ flex: 1, minWidth: 0, background: isDark ? '#243050' : '#f4f4ef', border: `1px solid ${border}`,
                      borderRadius: 6, padding: '7px 10px', color: textPri, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                  />
                  <button onClick={openCompose} title="New message"
                    style={{ width: 34, height: 34, flexShrink: 0, borderRadius: 6, border: 'none',
                      background: '#3498db', color: '#fff', fontSize: 19, fontWeight: 700, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, padding: 0 }}>
                    +
                  </button>
                </div>
              </div>

              <div style={{ flex: 1, overflowY: 'auto' }}>
                {loading ? (
                  <div style={{ padding: 20, textAlign: 'center', fontSize: 13, color: textSub, fontStyle: 'italic' }}>Loading…</div>
                ) : filtered.length === 0 ? (
                  <div style={{ padding: 20, textAlign: 'center', fontSize: 13, color: textSub, fontStyle: 'italic' }}>No conversations</div>
                ) : filtered.map(c => {
                  const isActive = c.customer_phone === selectedPhone;
                  const unread = Number(c.unread_count || 0) > 0;
                  return (
                    <div key={c.customer_phone} onClick={() => selectConversation(c.customer_phone)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                        borderBottom: `1px solid ${border}`, cursor: 'pointer',
                        // The selected row has to be obvious at a glance from
                        // across the counter, in both themes. The old tints
                        // (#0d1e2e / #eff6ff) were only a few points off the
                        // panel background and effectively invisible. The
                        // 3px accent bar is drawn as an inset shadow rather
                        // than a border so the row doesn't shift sideways by
                        // 3px when it becomes selected.
                        background: isActive ? (isDark ? '#1d3a5f' : '#d6e8fb') : 'transparent',
                        boxShadow: isActive ? 'inset 3px 0 0 #3498db' : 'none' }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                        background: itemBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 13, color: textPri, fontWeight: 600 }}>
                        {initials(c.customer_name)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, lineHeight: 1.5 }}>
                          <span style={{ fontWeight: (unread || isActive) ? 700 : 500, fontSize: 14, color: textPri }}>
                            {c.customer_name || formatPhoneDisplay(c.customer_phone)}
                          </span>
                          <span style={{ fontSize: 12, color: textSub, whiteSpace: 'nowrap' }}>{fmtListTime(c.last_created_at)}</span>
                        </div>
                        <div style={{ fontSize: 13, color: textSub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          lineHeight: 1.5, marginTop: 2 }}>
                          {c.last_direction === 'outbound' ? 'You: ' : ''}{c.last_body}
                        </div>
                      </div>
                      {c.has_failed && <i className="ti ti-alert-triangle" title="One or more messages failed to deliver" style={{ color: '#e74c3c', fontSize: 15, flexShrink: 0 }} aria-hidden="true" />}
                      {c.flagged && <i className="ti ti-star-filled" style={{ color: '#e67e22', fontSize: 15, flexShrink: 0 }} aria-hidden="true" />}
                      <button
                        onClick={(e) => toggleRowReadStatus(e, c.customer_phone, unread)}
                        title={unread ? 'Mark read' : 'Mark unread'}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', flexShrink: 0,
                          padding: '2px 4px', lineHeight: 1, fontSize: 16,
                          color: unread ? '#3498db' : textSub, opacity: unread ? 1 : 0.4 }}>
                        <i className={unread ? 'ti ti-mail' : 'ti ti-mail-opened'} aria-hidden="true" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Thread pane */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}
          onClickCapture={selected ? handleThreadInteraction : undefined}>
          {!selected ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: textSub, fontSize: 14, fontStyle: 'italic' }}>
              Select a conversation to view
            </div>
          ) : (
            <>
              <div style={{ padding: '12px 16px', borderBottom: `1px solid ${border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, color: textPri }}>{selected.customer_name || formatPhoneDisplay(selected.customer_phone)}</div>
                  {selected.customer_name && (
                    <div style={{ fontSize: 12, color: textSub, marginTop: 2 }}>{formatPhoneDisplay(selected.customer_phone)}</div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  {candidateTickets.length > 1 && (
                    <select value={selectedTicketId || ''} onChange={e => setSelectedTicketId(e.target.value)}
                      title="Multiple tickets found for this customer — pick which one templates and the invoice should use"
                      style={{ fontSize: 13, padding: '5px 8px', borderRadius: 6, border: `1px solid ${border}`,
                        background: isDark ? '#243050' : '#f4f4ef', color: textPri, cursor: 'pointer' }}>
                      {candidateTickets.map(t => (
                        <option key={t.id} value={t.id}>{t.displayId} — {t.bike} ({t.status})</option>
                      ))}
                    </select>
                  )}
                  <button onClick={Number(selected.unread_count) > 0 ? markSelectedRead : markSelectedUnread}
                    style={{ fontSize: 13, padding: '5px 10px', borderRadius: 6, border: `1px solid ${border}`,
                      background: 'transparent', color: textSub, cursor: 'pointer' }}>
                    {Number(selected.unread_count) > 0 ? 'Mark read' : 'Mark unread'}
                  </button>
                </div>
              </div>
              <div style={{ flex: 1, minHeight: 0, padding: '14px 16px', display: 'flex', flexDirection: 'column' }}>
                <SmsConversation
                  key={selected.customer_phone}
                  phone={selected.customer_phone}
                  ticketId={selectedTicketId}
                  customerId={selected.last_customer_id || null}
                  customerName={selected.customer_name || ''}
                  bikeLabel={candidateTickets.find(t => t.id === selectedTicketId)?.bike || selected.bike_label || ''}
                  balance={ticketDetail?.totalValue || 0}
                  ticketStatusCategory={ticketDetail?.statusCategory || null}
                  theme={theme}
                  markReadOnOpen={false}
                  onSent={() => setTimeout(loadConversations, 400)}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}